import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag, RefreshCw, Search } from "lucide-react";

export function PromoTab({
  products,
  categories,
  selectedProducts,
  setSelectedProducts,
  promoPrice,
  setPromoPrice,
  searchQuery,
  setSearchQuery,
  categoryFilter,
  setCategoryFilter,
  savingPromo,
  onApplyPromo,
  onRemovePromo,
}) {
  const filteredProducts = useMemo(() => products.filter(product => {
    const matchesSearch = !searchQuery ||
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || product.category_id === categoryFilter;
    return matchesSearch && matchesCategory;
  }), [products, searchQuery, categoryFilter]);

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllFiltered = () => {
    const filteredIds = filteredProducts.map(p => p.id);
    const allSelected = filteredIds.every(id => selectedProducts.includes(id));
    if (allSelected) {
      setSelectedProducts(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedProducts(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  return (
    <>
      <Card className="bg-red-500/10 border-red-500/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="text-red-200 text-sm">
                <strong>Prezzo Promo</strong>: visibile a tutti gli utenti quando attivo
              </p>
              <p className="text-red-200/70 text-xs mt-1">
                Seleziona i prodotti e imposta il prezzo promo. Lascia vuoto per rimuovere.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              type="text"
              placeholder="Cerca per nome o SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10"
            />
          </div>
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px] bg-[#18181b] border-white/10">
            <SelectValue placeholder="Tutte le categorie" />
          </SelectTrigger>
          <SelectContent className="bg-[#18181b] border-white/10">
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-[#18181b] border-white/5 overflow-x-auto">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Badge variant="outline" className="text-white border-white/20">
              {selectedProducts.length} selezionati
            </Badge>
            <div className="flex items-center gap-2">
              <span className="text-zinc-400 text-sm">Prezzo Promo:</span>
              <Input
                type="number" step="0.01"
                placeholder="€"
                value={promoPrice}
                onChange={(e) => setPromoPrice(e.target.value)}
                className="w-24 bg-[#27272a] border-red-500/30"
              />
            </div>
            <Button
              onClick={() => onApplyPromo(filteredProducts)}
              disabled={savingPromo || selectedProducts.length === 0}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {savingPromo ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />}
              Applica Promo
            </Button>
            <Button
              onClick={onRemovePromo}
              disabled={savingPromo || selectedProducts.length === 0}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              Rimuovi Promo
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#18181b] border-white/5 overflow-x-auto">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="w-12">
                  <Checkbox
                    checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedProducts.includes(p.id))}
                    onCheckedChange={selectAllFiltered}
                  />
                </TableHead>
                <TableHead className="text-zinc-400">Prodotto</TableHead>
                <TableHead className="text-zinc-400">SKU</TableHead>
                <TableHead className="text-zinc-400">Categoria</TableHead>
                <TableHead className="text-zinc-400 text-right">Listino</TableHead>
                <TableHead className="text-zinc-400 text-right text-red-400">Promo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.slice(0, 100).map((product) => (
                <TableRow key={product.id} className="border-white/5">
                  <TableCell>
                    <Checkbox
                      checked={selectedProducts.includes(product.id)}
                      onCheckedChange={() => toggleProductSelection(product.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium text-white">{product.name}</TableCell>
                  <TableCell className="font-mono text-zinc-400 text-sm">{product.sku}</TableCell>
                  <TableCell className="text-zinc-400">{product.category_name}</TableCell>
                  <TableCell className="text-right font-mono text-white">{product.price_list.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-mono">
                    {product.price_promo ? (
                      <Badge className="bg-red-500/20 text-red-400">{product.price_promo.toFixed(2)}</Badge>
                    ) : (
                      <span className="text-zinc-500">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredProducts.length > 100 && (
            <div className="p-4 text-center text-zinc-500 text-sm">
              Mostrando 100 di {filteredProducts.length} prodotti. Usa i filtri per affinare la ricerca.
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
